import { Fragment, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, RefreshCw } from 'lucide-react'
import { api } from '../../lib/api'
import { useSyncJob } from '../../lib/useSyncJob'
import { ClientSelect } from '../../components/ClientSelect'
import { useClientSelection } from '../../stores/clientSelection'
import { ListFilters, EMPTY_FILTERS, type FilterState } from '../../components/ListFilters'
import { COUNTRY_CURRENCY } from '@emag/shared'
import { fmtMoney, fmtDate } from '../../lib/format'

type Verdict = 'neajuns' | 'storno_lipsa' | 'pe_drum' | 'in_curs' | 'ok' | 'anulat'

interface Rma {
  emagRmaId: string
  status: number | null
  statusReason: number | null
  returnType: number | null
  pickupMethod: number | null
  incident: boolean
  date: string | null
  pickedAt: string | null
  receivedAt: string | null
  refundAmount: number | null
  refundType: string | null
  refundStatus: string | null
}
interface ReturnOrder {
  country: string
  emagOrderId: string
  customerName: string | null
  rmas: Rma[]
  verdict: Verdict
  ageDays: number | null
  refund: number
  refundDp: number | null
  storno: number
  stornoDocs: string[]
}
interface Resp {
  items: ReturnOrder[]
  counts: Record<string, number>
  graceDays: number
}

const TARI = ['RO', 'BG', 'HU'] as const
const STEAG: Record<string, string> = { RO: '🇷🇴', BG: '🇧🇬', HU: '🇭🇺' }

/** Ordinea = gravitatea; aceeasi ordine ca in returns.logic.ts. */
const VERDICT: Record<Verdict, { label: string; hint: string; cls: string }> = {
  neajuns: {
    label: 'Produs neajuns',
    hint: 'Banii s-au dat, produsul n-a ajuns înapoi peste prag sau returul e închis cu incident — de reclamat la eMAG',
    cls: 'bg-red-50 text-red-700 border-red-200',
  },
  storno_lipsa: {
    label: 'Lipsește storno',
    hint: 'Produsul a ajuns înapoi și s-au dat banii, dar nu există factură storno care să acopere suma',
    cls: 'bg-amber-50 text-amber-800 border-amber-200',
  },
  pe_drum: {
    label: 'Pe drum',
    hint: 'Retur rapid: banii s-au dat la ridicarea coletului, produsul încă n-a ajuns — se așteaptă',
    cls: 'bg-sky-50 text-sky-700 border-sky-200',
  },
  in_curs: { label: 'În curs', hint: 'Returul nu e încă finalizat', cls: 'bg-gray-50 text-gray-600 border-gray-200' },
  ok: { label: 'În regulă', hint: 'Produs ajuns înapoi și storno emis', cls: 'bg-green-50 text-green-700 border-green-200' },
  anulat: { label: 'Anulat / respins', hint: 'Nu s-a dat nimic înapoi — factura rămâne încasată', cls: 'bg-gray-50 text-gray-500 border-gray-200' },
}
const ORDINE = Object.keys(VERDICT) as Verdict[]

const STARE_RMA: Record<number, string> = { 1: 'incompletă', 2: 'nouă', 3: 'preluată', 4: 'respinsă', 5: 'anulată', 7: 'finalizată' }
const TIP_REFUND: Record<string, string> = { CO: 'card', COD: 'cont', VCR: 'voucher' }

function Badge({ v }: { v: Verdict }) {
  return (
    <span title={VERDICT[v].hint} className={`inline-block whitespace-nowrap rounded border px-2 py-0.5 text-[11px] ${VERDICT[v].cls}`}>
      {VERDICT[v].label}
    </span>
  )
}

export function RetururiPage() {
  const clientId = useClientSelection((s) => s.selectedClientId)
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS)
  const [country, setCountry] = useState<string>('')
  const [verdict, setVerdict] = useState<Verdict | ''>('')
  const [open, setOpen] = useState<Set<string>>(new Set())

  const { data, isLoading } = useQuery({
    queryKey: ['emag-returns', clientId, filters.dateFrom, filters.dateTo, country],
    queryFn: async () =>
      (
        await api.get<Resp>('/emag/returns', {
          params: {
            clientId,
            ...(country ? { country } : {}),
            ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
            ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
          },
        })
      ).data,
    enabled: clientId != null,
  })

  // Retururile vin odata cu comenzile — aceeasi sincronizare, pe intervalul ales.
  const sync = useSyncJob('orders', clientId, {
    successText: (r) => `Sincronizate ${r.synced ?? 0} comenzi`,
    invalidate: ['emag-returns', 'emag-orders'],
  })
  function startSync() {
    void sync.start({
      ...(filters.dateFrom ? { createdAfter: `${filters.dateFrom} 00:00:00` } : {}),
      ...(filters.dateTo ? { createdBefore: `${filters.dateTo} 23:59:59` } : {}),
      ...(country ? { country } : {}),
    })
  }

  const items = useMemo(() => {
    const q = filters.q.trim().toLowerCase()
    return (data?.items ?? [])
      .filter((i) => !verdict || i.verdict === verdict)
      .filter(
        (i) =>
          !q ||
          i.emagOrderId.includes(q) ||
          (i.customerName ?? '').toLowerCase().includes(q) ||
          i.rmas.some((r) => r.emagRmaId.includes(q)),
      )
      .sort((a, b) => ORDINE.indexOf(a.verdict) - ORDINE.indexOf(b.verdict))
  }, [data, verdict, filters.q])

  const toggle = (k: string) =>
    setOpen((s) => {
      const n = new Set(s)
      if (n.has(k)) n.delete(k)
      else n.add(k)
      return n
    })

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-b4l-text">Retururi</h1>
        <div className="flex items-center gap-2">
          <ClientSelect />
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="rounded-md border border-b4l-border px-2.5 py-1.5 text-[13px] outline-none focus:border-b4l-green"
          >
            <option value="">Toate țările</option>
            {TARI.map((t) => (
              <option key={t} value={t}>{STEAG[t]} {t}</option>
            ))}
          </select>
          <button
            onClick={startSync}
            disabled={clientId == null || sync.running}
            className="flex items-center gap-1.5 rounded-md bg-b4l-green px-3 py-1.5 text-[13px] font-medium text-white hover:bg-b4l-green-dark disabled:opacity-60"
          >
            <RefreshCw size={14} className={sync.running ? 'animate-spin' : ''} />
            {sync.running ? 'Sincronizare în curs…' : 'Sincronizează'}
          </button>
        </div>
      </div>

      <div className="mb-3">
        <ListFilters value={filters} onChange={setFilters} searchPlaceholder="nr. comandă, ID retur, client…" />
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2 text-[12px]">
        <button
          onClick={() => setVerdict('')}
          className={`rounded-md border px-3 py-1 ${verdict === '' ? 'border-b4l-green bg-b4l-green text-white' : 'border-b4l-border text-b4l-text-muted'}`}
        >
          Toate ({data?.items.length ?? 0})
        </button>
        {ORDINE.map((v) => (
          <button
            key={v}
            onClick={() => setVerdict(verdict === v ? '' : v)}
            title={VERDICT[v].hint}
            className={`rounded-md border px-3 py-1 ${verdict === v ? 'border-b4l-green bg-b4l-green text-white' : VERDICT[v].cls}`}
          >
            {VERDICT[v].label} ({data?.counts[v] ?? 0})
          </button>
        ))}
        {data && <span className="ml-auto text-b4l-text-dim">Prag „produs neajuns”: {data.graceDays} zile de la ridicare</span>}
      </div>

      <div className="rounded-xl border border-b4l-border bg-white">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="sticky top-0 z-10 border-b border-b4l-border bg-white text-left text-b4l-text-muted shadow-[0_1px_0_0_rgb(229,231,235)]">
              <th className="w-8 px-2 py-2.5" />
              <th className="px-3 py-2.5 font-medium">Țara</th>
              <th className="px-3 py-2.5 font-medium">Comandă</th>
              <th className="px-3 py-2.5 font-medium">Client</th>
              <th className="px-3 py-2.5 font-medium">Retururi</th>
              <th className="px-3 py-2.5 text-right font-medium">Rambursat</th>
              <th className="px-3 py-2.5 text-right font-medium" title="Refund din desfășurătoarele de plată importate">Refund DP</th>
              <th className="px-3 py-2.5 text-right font-medium">Storno</th>
              <th className="px-3 py-2.5 font-medium">Verdict</th>
            </tr>
          </thead>
          <tbody>
            {(isLoading || clientId == null) && (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-b4l-text-dim">
                  {clientId == null ? 'Alege un client.' : 'Se încarcă…'}
                </td>
              </tr>
            )}
            {!isLoading && clientId != null && items.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-b4l-text-dim">
                  Niciun retur. Alege perioada și apasă „Sincronizează".
                </td>
              </tr>
            )}
            {items.map((o) => {
              const k = `${o.country}|${o.emagOrderId}`
              const cur = COUNTRY_CURRENCY[o.country as keyof typeof COUNTRY_CURRENCY] ?? 'RON'
              const deschis = open.has(k)
              return (
                <Fragment key={k}>
                  <tr className="cursor-pointer border-b border-b4l-border hover:bg-b4l-bg-soft" onClick={() => toggle(k)}>
                    <td className="px-2 py-2.5 text-b4l-text-dim">{deschis ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">{STEAG[o.country] ?? ''} {o.country}</td>
                    <td className="px-3 py-2.5 font-medium">{o.emagOrderId}</td>
                    <td className="px-3 py-2.5">{o.customerName ?? '—'}</td>
                    <td className="px-3 py-2.5">{o.rmas.length}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">{o.refund ? fmtMoney(o.refund, cur) : '—'}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">{o.refundDp != null ? fmtMoney(o.refundDp, cur) : '—'}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap" title={o.stornoDocs.join(', ')}>
                      {o.storno ? fmtMoney(o.storno, cur) : '—'}
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge v={o.verdict} />
                      {o.ageDays != null && (o.verdict === 'pe_drum' || o.verdict === 'neajuns') && (
                        <span className="ml-1.5 text-[11px] text-b4l-text-dim">{o.ageDays} zile</span>
                      )}
                    </td>
                  </tr>
                  {deschis && (
                    <tr className="border-b border-b4l-border bg-b4l-bg-soft">
                      <td />
                      <td colSpan={8} className="px-3 py-2">
                        <table className="w-full text-[12px]">
                          <thead>
                            <tr className="text-left text-b4l-text-muted">
                              <th className="py-1 pr-3 font-medium">ID retur</th>
                              <th className="py-1 pr-3 font-medium">Creat</th>
                              <th className="py-1 pr-3 font-medium">Stare</th>
                              <th className="py-1 pr-3 font-medium">Predare</th>
                              <th className="py-1 pr-3 font-medium">Ridicat</th>
                              <th className="py-1 pr-3 font-medium">Recepționat</th>
                              <th className="py-1 pr-3 text-right font-medium">Rambursat</th>
                            </tr>
                          </thead>
                          <tbody>
                            {o.rmas.map((r) => (
                              <tr key={r.emagRmaId}>
                                <td className="py-1 pr-3">{r.emagRmaId}</td>
                                <td className="py-1 pr-3">{fmtDate(r.date)}</td>
                                <td className="py-1 pr-3">
                                  {r.status != null ? STARE_RMA[r.status] ?? r.status : '—'}
                                  {r.statusReason === 4 && <span className="text-b4l-text-dim"> (retur rapid)</span>}
                                  {r.incident && <span className="text-red-700"> · cu incident</span>}
                                </td>
                                <td className="py-1 pr-3">{r.pickupMethod === 4 ? 'locker' : r.pickupMethod === 1 ? 'ridicat de eMAG' : '—'}</td>
                                <td className="py-1 pr-3">{fmtDate(r.pickedAt) || '—'}</td>
                                <td className="py-1 pr-3">{fmtDate(r.receivedAt) || <span className="text-b4l-text-dim">nu</span>}</td>
                                <td className="py-1 pr-3 text-right whitespace-nowrap">
                                  {r.refundAmount != null ? fmtMoney(r.refundAmount, cur) : '—'}
                                  {r.refundType && <span className="text-b4l-text-dim"> · {TIP_REFUND[r.refundType] ?? r.refundType}</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {o.stornoDocs.length > 0 && (
                          <div className="mt-1 text-[12px] text-b4l-text-muted">Storno: {o.stornoDocs.join(', ')}</div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
